import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";

import { FiBookOpen } from "react-icons/fi";

import { isAnonymousUser, selectUser } from "redux/authReducer";
import {
  setShowBugReportModal,
  setShowLearnMoreModal,
  setshowProfileModal,
  submitBugReport,
} from "redux/Modals/actions";

import {
  PRIVATE_DATASETS_PATH,
  PUBLIC_DATASETS_PATH,
} from "helpers/constants/paths";

import InvitationModal from "views/modals/InvitationModal";
import ProfileModal from "views/modals/ProfileModal";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import VLDatasetIcon from "views/uikit/VLDatasetIcon";
import VLAvatar from "views/uikit/VLAvatar";
import VLIcon from "views/uikit/VLIcon";

import BugReporter from "assets/icons/BugReporter";
import LocalIcon from "assets/icons/LocalIcon";

import docsPdf from "assets/pdf/docs.pdf";
import styles from "./style.module.scss";
import { isFastdupUser, isOnPremUser } from "helpers/utility/utilities";
import BugReporterModal from "views/modals/BugReporterModal";
import TaskManagerIcon from "assets/icons/taskManagerIcon";
import ModelCatalogIcon from "assets/icons/ModelCatalogIcon";
import {
  getIsModelsCatalogEnabled,
  getIsUserManagementEnabled,
} from "redux/User/selectors";
import { Typography } from "antd";
import classNames from "classnames";
import { setSeverityData } from "redux/SingleDataset/actions";
import SettingsIcon from "assets/icons/SettingsIcon";

const Sidebar = ({
  userAvatar,
  showBugReporter,
}: {
  userAvatar: string | undefined;
  showBugReporter: boolean;
}) => {
  const [isLocalTooltipOpen, setIsLocalTooltipOpen] = useState(false);
  const pathName = useLocation().pathname;
  const user = selectUser();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const vlVersion = localStorage.getItem("vlVersion") || null;
  const isAllowedUser = (user && !isAnonymousUser()) || isOnPremUser();

  const handleAvatarClick = () => {
    if (isAllowedUser) {
      dispatch(setshowProfileModal(true));
    }
  };

  const handleLogoClick = () => {
    dispatch(setSeverityData(null));
    if (isAllowedUser) {
      navigate(PRIVATE_DATASETS_PATH);
    } else {
      navigate(PUBLIC_DATASETS_PATH);
    }
  };

  const handleDocsButtonClick = (e: any) => {
    e.stopPropagation();
  };

  const handleLearnMoreButtonClick = (e: any) => {
    setIsLocalTooltipOpen(false);
    dispatch(setShowLearnMoreModal(true));
  };

  const handleBugReporterClick = () => {
    dispatch(setShowBugReportModal(true));
  };

  const avatarClass = isAllowedUser ? styles.avatarContainer : styles.noUser;
  const showBugReporterIcon = userInfo?.isInternalUser;
  const isModelsCatalogEnabled = useSelector(getIsModelsCatalogEnabled);
  const isUserManagementEnabled = useSelector(getIsUserManagementEnabled);
  const { Link } = Typography;

  const isOnTaskManagerPage = pathName === "/task-manager";
  const isOnModelsCatalogPage = pathName === "/models-catalog";
  const isWorkspaceSettingsPage = pathName === "/workspace-settings";

  const isOnDatasetsPage =
    pathName === PRIVATE_DATASETS_PATH ||
    pathName === PUBLIC_DATASETS_PATH ||
    pathName.startsWith("/dataset/");

  return (
    <div className={classNames(styles.sidebar)}>
      <div className={styles.sidebarContainer}>
        <div className={styles.sidebarTop}>
          <span className={styles.vlLogo} onClick={handleLogoClick}>
            <VLIcon />
          </span>
          <div
            className={classNames(styles.menuItem, {
              [styles.active]: isOnDatasetsPage,
            })}
          >
            <VLDatasetIcon
              classNames={`${styles.datasetIcon} ${isOnDatasetsPage ? styles.active : ""}`}
            />
          </div>
          <div
            className={classNames(styles.menuItem, {
              [styles.active]: isOnTaskManagerPage,
            })}
            data-tooltip-id="task-manager-icon-tooltip"
            data-tooltip-place="right"
          >
            <Link
              className={`${styles.taskManagerIcon} ${isOnTaskManagerPage ? styles.active : ""}`}
              onClick={() => navigate("/task-manager")}
            >
              <TaskManagerIcon />
              <TooltipWrapper id="task-manager-icon-tooltip">
                <div className={styles.tooltipText}>Task manager</div>
              </TooltipWrapper>
            </Link>
          </div>
          {isModelsCatalogEnabled && (
            <div
              className={classNames(styles.menuItem, {
                [styles.active]: isOnModelsCatalogPage,
              })}
              data-tooltip-id="models-catalog-icon-tooltip"
              data-tooltip-place="right"
            >
              <Link
                className={`${styles.modelsCatalogIcon} ${isOnModelsCatalogPage ? styles.active : ""}`}
                onClick={() => navigate("/models-catalog")}
              >
                <ModelCatalogIcon />
                <TooltipWrapper id="models-catalog-icon-tooltip">
                  <div className={styles.tooltipText}>Models Catalog</div>
                </TooltipWrapper>
              </Link>
            </div>
          )}
        </div>

        <div className={styles.sidebarBottom}>
          {isOnPremUser() && isUserManagementEnabled && (
            <div
              className={classNames(styles.menuItem, styles.settingIcon, {
                [styles.active]: isWorkspaceSettingsPage,
              })}
              data-tooltip-id="workspace-settings-icon-tooltip"
              data-tooltip-place="right"
            >
              <Link
                className={`${styles.workspaceSettingIcon} ${isWorkspaceSettingsPage ? styles.active : ""}`}
                onClick={() => navigate("/workspace-settings")}
              >
                <SettingsIcon />
                <TooltipWrapper id="workspace-settings-icon-tooltip">
                  <div className={styles.tooltipText}>Workspace Settings</div>
                </TooltipWrapper>
              </Link>
            </div>
          )}

          <div
            className={styles.docsIcon}
            data-tooltip-content={"Go to documentation"}
            data-tooltip-id="docs-icon-tooltip"
            data-tooltip-place="right"
          >
            <a
              href={docsPdf}
              target="_blank"
              rel="noreferrer"
              onClick={handleDocsButtonClick}
            >
              <FiBookOpen size="30" />
            </a>
            <TooltipWrapper id="docs-icon-tooltip" />
          </div>
          {isFastdupUser() && (
            <div
              className={styles.localIcon}
              data-tooltip-id="local-icon-tooltip"
              data-tooltip-place="right"
            >
              <LocalIcon />
              <TooltipWrapper
                id="local-icon-tooltip"
                maxWidth="420px"
                isOpen={isLocalTooltipOpen}
                setIsOpen={setIsLocalTooltipOpen}
                isClickable={true}
              >
                <div className={styles.localTooltip}>
                  <div className={styles.tooltipText}>
                    <b>Local visual layer deployment</b>
                    <br />
                    Your Data Never Leaves This Environment
                  </div>
                  <button
                    className={styles.learnMoreButton}
                    onClick={handleLearnMoreButtonClick}
                  >
                    LEARN MORE
                  </button>
                </div>
              </TooltipWrapper>
            </div>
          )}
          {showBugReporterIcon && (
            <div
              className={styles.bugReporterIcon}
              onClick={handleBugReporterClick}
            >
              <BugReporter />
            </div>
          )}
          <div className={avatarClass} onClick={handleAvatarClick}>
            <VLAvatar size="small" userAvatar={userAvatar} />
            {!!vlVersion && (
              <div className={styles.vlVersionBanner} title={vlVersion}>
                {vlVersion}
              </div>
            )}
          </div>
        </div>
        <ProfileModal userAvatar={userAvatar} />
        <InvitationModal />
        {showBugReporter && (
          <BugReporterModal
            onClose={() => {
              dispatch(setShowBugReportModal(false));
            }}
            onSubmit={async ({
              title,
              description,
            }: {
              title: string;
              description?: string;
            }) => {
              await dispatch(submitBugReport({ title, description }) as any);
              dispatch(setShowBugReportModal(false));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Sidebar;
